import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';

const apiUrl = process.env.REACT_APP_API_URL;

const EditFarrierRecord = () => {
  const { t } = useTranslation();
  const { record_id } = useParams();
  const navigate = useNavigate();
  const [proceduresList, setProceduresList] = useState([]);
  const [formData, setFormData] = useState({
    record_date: '',
    work_place: 'Inside',
    horse_name: '',
    rfid_chip_number: '',
    total_amount: 0,
    payment_type_id: '',
    notes: '',
    procedures: [],
    medications: [],
  });
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [medicationsList, setMedicationsList] = useState([]);

  const fetchFarrierRecord = useCallback(async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/farrier-records/${record_id}`
      );
      let recordData = response.data;

      if (typeof recordData.file_uploads === 'string') {
        recordData.file_uploads = recordData.file_uploads
          ? recordData.file_uploads.split(';')
          : [];
      }

      if (recordData.procedures) {
        recordData.procedures = recordData.procedures.split(';').map((proc) => {
          const [procedure_name, price] = proc.split('::');
          const match = proceduresList.find(
            (p) =>
              p.procedure_name.trim().toLowerCase() ===
              procedure_name.trim().toLowerCase()
          );

          return {
            procedure_id: match?.procedure_id || '',
            procedure_name: match?.procedure_name || procedure_name,
            price: parseFloat(price) || 0,
          };
        });
      }

      if (typeof recordData.medications === 'string') {
        recordData.medications = recordData.medications
          .split(';')
          .map((med) => {
            const [medication_id_raw, dosage, quantity, unit, price] =
              med.split('::');
            const match = medicationsList.find(
              (m) => m.medication_id.toString() === medication_id_raw
            );

            return {
              medication_id:
                match?.medication_id || parseInt(medication_id_raw),
              medication_name: match?.medication_name || '',
              dosage,
              quantity,
              unit,
              price: parseFloat(price) || 0,
            };
          });
      } else if (!Array.isArray(recordData.medications)) {
        recordData.medications = [];
      }

      setFormData(recordData);
    } catch (error) {
      console.error('Error fetching farrier record', error);
    }
  }, [record_id, medicationsList, proceduresList]);

  const fetchPaymentTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/payment-types`);
      setPaymentTypes(response.data);
    } catch (error) {
      console.error('Error fetching payment types', error);
    }
  }, []);
  const fetchProceduresList = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/farrier_procedures`);
      setProceduresList(response.data);
    } catch (error) {
      console.error('Error fetching procedures list:', error);
    }
  }, []);
  const fetchMedicationsList = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/farrier_materials`);
      setMedicationsList(response.data);
    } catch (error) {
      console.error('Error fetching medications list:', error);
    }
  }, []);
  useEffect(() => {
    const loadData = async () => {
      await fetchPaymentTypes();
      await fetchProceduresList();
      await fetchMedicationsList();
    };

    loadData();
  }, [fetchPaymentTypes, fetchProceduresList, fetchMedicationsList]);

  useEffect(() => {
    if (proceduresList.length > 0 && medicationsList.length > 0) {
      fetchFarrierRecord();
    }
  }, [proceduresList, medicationsList, fetchFarrierRecord]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanedData = {
        ...formData,
        procedures: Array.isArray(formData.procedures)
          ? formData.procedures.map((p) => ({
              ...p,
              price: parseFloat(p.price) || 0,
            }))
          : [],
        medications: Array.isArray(formData.medications)
          ? formData.medications.map((m) => ({
              ...m,
              price: parseFloat(m.price) || 0,
            }))
          : [],
      };
      const proceduresTotal = cleanedData.procedures.reduce(
        (sum, procedure) => sum + (Number(procedure.price) || 0),
        0
      );
      const materialsTotal = cleanedData.medications.reduce(
        (sum, material) => sum + (Number(material.price) || 0),
        0
      );
      cleanedData.total_amount = proceduresTotal + materialsTotal;

      await axios.put(`${apiUrl}/farrier-records/${record_id}`, cleanedData);

      alert('Record updated successfully!');
      navigate('/FarrierList');
    } catch (error) {
      console.error('Error updating record', error);
    }
  };

  /*const handleProcedureChange = (index, field, value) => {
    const updated = [...formData.procedures];
    updated[index][field] = value;
    setFormData({ ...formData, procedures: updated });
  };*/

  const handleMedicationChange = (index, field, value) => {
    const updated = [...formData.medications];
    updated[index][field] = value;
    setFormData({ ...formData, medications: updated });
  };
  /*const addProcedure = () => {
    setFormData({
      ...formData,
      procedures: [...formData.procedures, { procedure_name: '', price: '' }],
    });
  };*/

  const addMedication = () => {
    setFormData({
      ...formData,
      medications: [
        ...(Array.isArray(formData.medications) ? formData.medications : []),
        {
          medication_id: '',
          dosage: '',
          quantity: '',
          unit: '',
          price: '',
        },
      ],
    });
  };

  /* const deleteProcedure = (index) => {
    const updated = formData.procedures.filter((_, i) => i !== index);
    setFormData({ ...formData, procedures: updated });
  };*/

  /*const deleteMedication = (index) => {
    const updated = formData.medications.filter((_, i) => i !== index);
    setFormData({ ...formData, medications: updated });
  };*/
  const removeMedication = (index) => {
    const updated = [...formData.medications];
    updated.splice(index, 1);
    setFormData({ ...formData, medications: updated });
  };

  return (
    <Box p={2} maxWidth={800} mx="auto">
      <Typography variant="h4" gutterBottom>
        Edit Farrier Record
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Horse Name"
          value={formData.horse_name}
          fullWidth
          margin="normal"
          disabled
        />
        <TextField
          label="RFID Chip Number"
          value={formData.rfid_chip_number}
          fullWidth
          margin="normal"
          disabled
        />

        {false && (
          <>
            <TextField
              label="Total Amount"
              type="number"
              value={formData.total_amount}
              onChange={(e) =>
                setFormData({ ...formData, total_amount: e.target.value })
              }
              fullWidth
              margin="normal"
            />
          </>
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel>Payment Type</InputLabel>
          <Select
            value={formData.payment_type_id || ''}
            onChange={(e) =>
              setFormData({ ...formData, payment_type_id: e.target.value })
            }
            label="Payment Type"
          >
            {paymentTypes.map((type) => (
              <MenuItem key={type.payment_type_id} value={type.payment_type_id}>
                {type.payment_type_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl component="fieldset" margin="normal">
          <Typography variant="h6" mt={2}>
            Procedures
          </Typography>
          <Grid container spacing={2}>
            {proceduresList.map((procedure) => (
              <Grid item xs={12} sm={6} md={4} key={procedure.procedure_id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={procedure.procedure_id}
                      checked={formData.procedures?.some(
                        (p) => p.procedure_id === procedure.procedure_id
                      )}
                      onChange={() => {
                        const exists = formData.procedures?.some(
                          (p) => p.procedure_id === procedure.procedure_id
                        );
                        const updatedProcedures = exists
                          ? formData.procedures.filter(
                              (p) => p.procedure_id !== procedure.procedure_id
                            )
                          : [
                              ...(formData.procedures || []),
                              {
                                procedure_id: procedure.procedure_id,
                                price: procedure.price || 0,
                              },
                            ];

                        setFormData({
                          ...formData,
                          procedures: updatedProcedures,
                        });
                      }}
                    />
                  }
                  label={procedure.procedure_name}
                />
              </Grid>
            ))}
          </Grid>
        </FormControl>

        <FormControl component="fieldset" margin="normal">
          <Typography variant="h6" mt={2}>
            {t('farrierMaterials.title')}
          </Typography>

          {formData.medications.map((med, idx) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={idx}
              sx={{ mb: 2 }}
            >
              {/* اسم الدواء */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Medication</InputLabel>
                  <Select
                    value={med.medication_id || ''}
                    label="Medication"
                    onChange={(e) => {
                      const selected = medicationsList.find(
                        (m) =>
                          m.medication_id.toString() ===
                          e.target.value.toString()
                      );
                      handleMedicationChange(
                        idx,
                        'medication_id',
                        selected?.medication_id || ''
                      );
                      handleMedicationChange(
                        idx,
                        'medication_name',
                        selected?.medication_name || ''
                      );
                      handleMedicationChange(
                        idx,
                        'dosage',
                        selected?.default_dosage || ''
                      );
                      handleMedicationChange(
                        idx,
                        'quantity',
                        selected?.default_quantity || 1
                      );
                      handleMedicationChange(
                        idx,
                        'unit',
                        selected?.default_unit || ''
                      );
                      handleMedicationChange(
                        idx,
                        'price',
                        selected?.price || 0
                      );
                    }}
                  >
                    {medicationsList.map((m) => (
                      <MenuItem key={m.medication_id} value={m.medication_id}>
                        {m.medication_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* الجرعة */}
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Dosage"
                  value={med.dosage}
                  onChange={(e) =>
                    handleMedicationChange(idx, 'dosage', e.target.value)
                  }
                  fullWidth
                />
              </Grid>

              {/* الكمية */}
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Quantity"
                  value={med.quantity}
                  onChange={(e) =>
                    handleMedicationChange(idx, 'quantity', e.target.value)
                  }
                  fullWidth
                />
              </Grid>

              {/* الوحدة */}
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Unit"
                  value={med.unit}
                  onChange={(e) =>
                    handleMedicationChange(idx, 'unit', e.target.value)
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={1}>
                <IconButton color="error" onClick={() => removeMedication(idx)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Button onClick={addMedication} startIcon={<Add />}>
            Add Medication
          </Button>
        </FormControl>

        <TextField
          label="Notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          fullWidth
          margin="normal"
          multiline
          rows={4}
        />
        <Box mt={2}>
          <Button type="submit" variant="contained" color="primary">
            Update Record
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EditFarrierRecord;
